import React, { useState } from 'react';
import { Stack, Card } from 'braid-design-system';

import PageLoader from '../../common/PageLoader';
import Error from '../../common/Error/Error';
import AddBrandPanel from './AddBrandPanel';
import BrandListControls from './BrandListControls';
import CardGrid from './CardGrid';
import NoBrandResults from './NoBrandResults';

import { filterWithSearchTerm } from '../utils/filterWithSearchTerm';
import type { BrandListItem } from 'src/types';
import type { ApolloError } from '@apollo/client';
import type { BrandOwner } from 'src/App/generated/graphql-types';

import { useTranslations } from '@vocab/react';
import translations from './.vocab';

const BrandListingContainer = ({
  brands,
  loading,
  error,
  owner,
}: {
  owner?: BrandOwner;
  brands: BrandListItem[];
  loading: boolean;
  error?: ApolloError;
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredBrands = filterWithSearchTerm(brands, searchTerm);
  const hasBrands = brands.length > 0;
  const hasFilteredBrands = filteredBrands.length > 0;
  const hasNoMatchingBrands = !hasFilteredBrands && searchTerm.length > 0;

  const { t } = useTranslations(translations);

  if (loading || !owner?.id) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Card>
        <Error message={t('Could not load brands')} />
      </Card>
    );
  }

  return (
    <Stack space="medium">
      {hasBrands && <BrandListControls onSearchBrands={setSearchTerm} />}
      {!hasBrands && !loading && <AddBrandPanel />}
      {hasFilteredBrands && <CardGrid brands={filteredBrands} />}
      {hasNoMatchingBrands && <NoBrandResults searchTerm={searchTerm} />}
    </Stack>
  );
};

const BrandListingWithAccount = BrandListingContainer;

export default BrandListingWithAccount;
